import type { ErroApi } from '@wms/shared';

/**
 * Erro de API com status preservado.
 * O status importa para a UI: 401 volta para o login, 403 mostra "sem
 * permissão" (não é erro do usuário), 503 sugere tentar de novo.
 */
export class ErroHttp extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly detalhes: unknown;

  constructor(status: number, corpo: Partial<ErroApi>) {
    super(corpo.mensagem ?? 'Não foi possível falar com o servidor.');
    this.name = 'ErroHttp';
    this.status = status;
    this.codigo = corpo.erro ?? 'desconhecido';
    this.detalhes = corpo.detalhes;
  }

  get naoAutenticado(): boolean {
    return this.status === 401;
  }

  get semPermissao(): boolean {
    return this.status === 403;
  }
}

type Parametros = Record<string, string | number | boolean | null | undefined>;

function montarUrl(caminho: string, params?: Parametros): string {
  if (!params) return caminho;
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(params)) {
    if (valor === null || valor === undefined || valor === '') continue;
    busca.set(chave, String(valor));
  }
  const qs = busca.toString();
  return qs ? `${caminho}?${qs}` : caminho;
}

function seguroJson(texto: string): unknown {
  try {
    return JSON.parse(texto) as unknown;
  } catch {
    return { erro: 'resposta_invalida', mensagem: 'O servidor devolveu uma resposta inesperada.' };
  }
}

async function requisitar<T>(caminho: string, init: RequestInit, params?: Parametros): Promise<T> {
  let resposta: Response;
  try {
    resposta = await fetch(montarUrl(caminho, params), {
      ...init,
      credentials: 'include', // a sessão é cookie httpOnly
      headers: { Accept: 'application/json', ...(init.headers ?? {}) },
    });
  } catch {
    throw new ErroHttp(0, {
      erro: 'rede',
      mensagem: 'Sem resposta do servidor. Verifique a conexão.',
    });
  }

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  const corpo: unknown = texto ? seguroJson(texto) : null;

  if (!resposta.ok) {
    throw new ErroHttp(resposta.status, (corpo ?? {}) as Partial<ErroApi>);
  }
  return corpo as T;
}

export const api = {
  get: <T>(caminho: string, params?: Parametros): Promise<T> =>
    requisitar<T>(caminho, { method: 'GET' }, params),

  post: <T>(caminho: string, corpo?: unknown, params?: Parametros, sinal?: AbortSignal): Promise<T> =>
    requisitar<T>(
      caminho,
      {
        method: 'POST',
        headers: corpo === undefined ? {} : { 'Content-Type': 'application/json' },
        ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
        ...(sinal ? { signal: sinal } : {}),
      },
      params,
    ),

  put: <T>(caminho: string, corpo?: unknown, params?: Parametros): Promise<T> =>
    requisitar<T>(
      caminho,
      {
        method: 'PUT',
        headers: corpo === undefined ? {} : { 'Content-Type': 'application/json' },
        ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
      },
      params,
    ),

  patch: <T>(caminho: string, corpo?: unknown, params?: Parametros): Promise<T> =>
    requisitar<T>(
      caminho,
      {
        method: 'PATCH',
        headers: corpo === undefined ? {} : { 'Content-Type': 'application/json' },
        ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
      },
      params,
    ),

  /** `delete` é palavra reservada; o método HTTP continua DELETE. */
  del: <T>(caminho: string, params?: Parametros): Promise<T> =>
    requisitar<T>(caminho, { method: 'DELETE' }, params),
};
